import React, { useState, useRef, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  PhotoIcon,
  ChevronDownIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { storage, db } from '../firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, setDoc, onSnapshot, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { UserContext } from '../App';
import ZoomableImageModal from "./ZoomableImageModal";
import UnifiedJournalPrototype from "./UnifiedJournalPrototype";
import ConfigurableJournalInput from "./ConfigurableJournalInput";
import { readUnifiedJournalBetaCache, writeUnifiedJournalBetaCache } from "../utils/journalMode";
import { isTapeReading } from '../utils/tradeHelpers';
import { getClipboardImageFiles, readImageFilesAsDataUrls } from '../utils/imageInput';
import { getPnlTone } from '../utils/pnlTone';
import { getDurationSuggestion, getFormAutoDuration } from '../utils/formAutoDuration';
import {
  CHART_DROP_HIGHLIGHT_STYLE,
  useChartDropZone,
  useDocumentImagePaste,
  useRememberedSectionOpen,
  useSubmitShortcut,
  useUnsavedChangesGuard,
} from '../utils/entryFormHooks';
import {
  getDefaultTradeEnvFields,
  initializeTradeEnvState,
  resolveTradeEnvFields,
} from '../utils/tradeEnvFields';
import {
  buildTradeEnvForNewEntry,
  decodeAlignmentValue,
  isAlignmentField,
} from '../utils/tradeEnvAlignment';
import { ScreenshotUploadError, uploadScreenshotList } from '../utils/entryScreenshotUpload';
import { useToast } from './ui';
import {
  getDefaultJournalFields,
  getJournalField,
  resolveJournalFields,
} from '../utils/journalFields';

const tickerOptions = ["MES", "MNQ", "BTC", "GL", "SL", "MYM"];
const initialState = {
  title: "",
  ticker: "",
  pnl: "",
  rr: "",
  notes: "",
  accountBalance: "",
  duration: "",
  entryTime: "",
  exitTime: "",
  tickerTraded: "MNQ",
  tradeSession: "NY-AM",
};



const entryTypeOptions = [
  { value: 'trade', label: 'Trade' },
  { value: 'tape', label: 'Tape Reading' },
];

const MINDSET_EMOTIONS = [
  'Calm',
  'Focused',
  'Confident',
  'Greedy',
  'Hesitant',
  'Frustrated',
  'Discouraged',
  'Tilted',
];

const EXECUTION_OPTIONS = [
  'Perfectly executed',
  'Overtraded',
  'Over-risked',
  'Revenge traded',
  'Early exit',
  'Late exit',
  'Late entry',
  'Chased price',
  'Moved stop loss',
  'Impulsive trade',
  'Hesitated',
];

const createEmptyMindsetExecution = () => ({
  emotions: [],
  composure: null,
  execution: '',
  learned: '',
  distraction: '',
  improvement: '',
});

const hasMindsetExecutionData = (value) => (
  value.emotions.length > 0 ||
  value.composure !== null ||
  value.execution.trim() !== '' ||
  value.learned.trim() !== '' ||
  value.distraction.trim() !== '' ||
  value.improvement.trim() !== ''
);

function Accordion({ title, icon, defaultOpen = false, children }) {
  const { currentUser } = useContext(UserContext) || {};
  // Sections the user opened last time open again (per user, on this device).
  const [isOpen, toggleOpen] = useRememberedSectionOpen(title, defaultOpen, currentUser?.uid);
  
  return (
    <div className="bg-white dark:bg-[#1F2937] rounded-card border border-[#E5E7EB] dark:border-[#374151] shadow-subtle overflow-hidden">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={toggleOpen}
        className="w-full p-4 flex items-center justify-between hover:bg-[#F9FAFB] dark:hover:bg-[#374151] transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon && <div className="p-2 bg-[#F3F4F6] dark:bg-[#374151] rounded-card">{icon}</div>}
          <span className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB]">{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className="w-5 h-5 text-[#6B7280] dark:text-[#9CA3AF]" />
        </motion.div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="p-6 border-t border-[#E5E7EB] dark:border-[#374151]">
          {children}
        </div>
      </motion.div>
    </div>
  );
}

function SegmentedPill({ value, onChange, label }) {
  // Handle three states: true (Yes), false (No), null (N/A)
  const getButtonClass = (buttonValue) => {
    const baseClass = "flex-1 h-12 text-sm font-medium transition-colors";
    if (value === buttonValue) {
      if (buttonValue === true) {
        return `${baseClass} theme-success-soft`;
      } else if (buttonValue === false) {
        return `${baseClass} theme-danger-soft`;
      } else {
        return `${baseClass} bg-[#F3F4F6] dark:bg-[#374151] text-[#6B7280] dark:text-[#D1D5DB]`;
      }
    } else {
      return `${baseClass} bg-white dark:bg-[#1F2937] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#F9FAFB] dark:hover:bg-[#374151] border border-[#E5E7EB] dark:border-[#374151]`;
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB] flex items-center gap-2">
          {label}
        </label>
      )}
      <div className="flex rounded-btn overflow-hidden border border-[#E5E7EB] dark:border-[#374151]">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={getButtonClass(true)}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`${getButtonClass(false)} border-l border-r border-[#E5E7EB] dark:border-[#374151]`}
        >
          No
        </button>
        <button
          type="button"
          onClick={() => onChange(null)}
          className={getButtonClass(null)}
        >
          N/A
        </button>
      </div>
    </div>
  );
}

function trimFirestoreDoc(doc) {
  const MAX_STRING = 1000000;
  const MAX_ARRAY = 1000;
  const MAX_FIELD_BYTES = 1048487;
  function trim(obj) {
    if (Array.isArray(obj)) {
      let arr = obj.slice(0, MAX_ARRAY);
      arr = arr.map(trim);
      if (JSON.stringify(arr).length > MAX_FIELD_BYTES) return undefined;
      return arr;
    } else if (typeof obj === 'string') {
      let s = obj.slice(0, MAX_STRING);
      if (s.length > MAX_FIELD_BYTES) return undefined;
      return s;
    } else if (typeof obj === 'object' && obj !== null) {
      const out = {};
      for (const k in obj) {
        const trimmed = trim(obj[k]);
        if (trimmed !== undefined) out[k] = trimmed;
      }
      if (JSON.stringify(out).length > MAX_FIELD_BYTES) return undefined;
      return out;
    } else {
      return obj;
    }
  }
  return trim(doc);
}

// Helper function to calculate account balance
function calculateAccountBalance(previousBalance, pnl) {
  const prev = Number(previousBalance) || 0;
  const pnlValue = Number(pnl) || 0;
  return (Math.round((prev + pnlValue) * 100) / 100).toFixed(2);
}

const JournalEntryForm = ({
  onSave,
  onCancel,
  onGoToQuick,
  initialAccountBalance,
  initialYear,
  initialMonth,
  initialDay,
  initialJournalMode = null,
}) => {
  const { currentUser, selectedAccount, showICTEmail, showBandzEmail, setEliteBadgeAchieved, setICTEmailFound, easterEggs, triggerDataRefresh } = useContext(UserContext);
  const toast = useToast();
  const [form, setForm] = useState({ ...initialState, accountBalance: initialAccountBalance ?? "" });
  const pnlTone = getPnlTone(form.pnl);
  const pnlToneColor = pnlTone === 'profit'
    ? 'var(--theme-success)'
    : pnlTone === 'loss'
      ? 'var(--theme-loss)'
      : 'var(--theme-text)';
  const [screenshots, setScreenshots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copyingLastTrade, setCopyingLastTrade] = useState(false);
  const [entryType, setEntryType] = useState('trade');
  const [tradeRating, setTradeRating] = useState('');
  const [mindsetExecutionBeta, setMindsetExecutionBeta] = useState(false);
  const [unifiedJournalBeta, setUnifiedJournalBeta] = useState(readUnifiedJournalBetaCache);
  const [journalModeOverride, setJournalModeOverride] = useState(initialJournalMode);
  const [mindsetExecution, setMindsetExecution] = useState(createEmptyMindsetExecution);
  const [rulesResponseForDate, setRulesResponseForDate] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const fileInputRef = useRef();
  const formRef = useRef(null);
  // True once the user types their own duration; it is then never recalculated over.
  const [durationEdited, setDurationEdited] = useState(false);
  // After "Save & add another" the next entry continues from the balance just saved.
  const savedBalanceRef = useRef(null);

  const getTodayDateString = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const [todayDateKey, setTodayDateKey] = useState(getTodayDateString());

  // Initialize date state from props or current date
  const getInitialDate = () => {
    if (initialYear && initialMonth && initialDay) {
      // Convert to YYYY-MM-DD format for date input
      const year = String(initialYear).padStart(4, '0');
      const month = String(initialMonth).padStart(2, '0');
      const day = String(initialDay).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Default to current date
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [logDate, setLogDate] = useState(getInitialDate());
  // The date a fresh form starts on (kept by "Save & add another").
  const [pristineDate, setPristineDate] = useState(logDate);
  const rulesDateKey = logDate || todayDateKey;
  const [swingEntryDate, setSwingEntryDate] = useState(getInitialDate());
  
  // Trading style state: 'day' or 'swing'
  const [tradingStyle, setTradingStyle] = useState('day');
  
  // Swing trading exit date state
  const [exitDate, setExitDate] = useState('');

  const defaultTradeEnvFields = getDefaultTradeEnvFields();
  const defaultJournalFields = getDefaultJournalFields();

  const [tradeEnvFields, setTradeEnvFields] = useState(defaultTradeEnvFields);
  const [journalFields, setJournalFields] = useState(defaultJournalFields);

  const [tradeEnv, setTradeEnv] = useState(() => 
    initializeTradeEnvState(defaultTradeEnvFields)
  );

  // Add new state for PD array
  const [pdArray, setPdArray] = useState("");

  // Add new state for what you were targeting
  const [target, setTarget] = useState("");

  // Add new state for economic release
  const [economicRelease, setEconomicRelease] = useState("");
  const [tickerSuggestions, setTickerSuggestions] = useState([]);
  const [showTickerSuggestions, setShowTickerSuggestions] = useState(false);

  // Load custom field definitions
  useEffect(() => {
    if (!currentUser) return;
    
    const loadSettings = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          setMindsetExecutionBeta(data.mindsetExecutionBeta === true);
          const loadedUnifiedJournalBeta = data.unifiedJournalBeta !== false;
          setUnifiedJournalBeta(loadedUnifiedJournalBeta);
          writeUnifiedJournalBetaCache(loadedUnifiedJournalBeta);
          const fieldsToUse = resolveTradeEnvFields(data);
          setTradeEnvFields(fieldsToUse);
          setTradeEnv(initializeTradeEnvState(fieldsToUse));
          setJournalFields(resolveJournalFields(data));
        } else {
          setUnifiedJournalBeta(true);
          writeUnifiedJournalBetaCache(true);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    loadSettings();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !rulesDateKey) return;
    const rulesDocRef = doc(db, 'users', currentUser.uid, 'rulesTracking', rulesDateKey);
    const unsubscribe = onSnapshot(rulesDocRef, (rulesSnap) => {
      if (rulesSnap.exists()) {
        setRulesResponseForDate(rulesSnap.data().response || null);
      } else {
        setRulesResponseForDate(null);
      }
    }, (error) => {
      console.error('Error listening to rules for date:', error);
    });
    return () => unsubscribe();
  }, [currentUser, rulesDateKey]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentDate = getTodayDateString();
      setTodayDateKey(prev => (prev === currentDate ? prev : currentDate));
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const handleRulesResponse = async (response) => {
    if (!currentUser) return;
    try {
      const rulesDocRef = doc(db, 'users', currentUser.uid, 'rulesTracking', rulesDateKey);
      await setDoc(rulesDocRef, {
        response,
        date: rulesDateKey,
        timestamp: new Date().toISOString()
      }, { merge: true });
      setRulesResponseForDate(response);
    } catch (error) {
      console.error('Error saving rules response:', error);
    }
  };

  // Helper to auto-format time as HH:MM (24-hour format)
  function formatTimeInput(value) {
    // Remove non-digits
    let v = value.replace(/\D/g, '');
    if (v.length <= 2) return v;
    if (v.length === 3) return v[0] + ':' + v.slice(1);
    if (v.length === 4) return v.slice(0, 2) + ':' + v.slice(2);
    return v.slice(0, 2) + ':' + v.slice(2, 4);
  }

  // Duration the form fills in from the entry/exit dates and times (null = not enough yet).
  const autoDurationFor = (times, entryDate, exitDt) => getFormAutoDuration({
    tradingStyle,
    entryDate,
    exitDate: exitDt,
    entryTime: times.entryTime,
    exitTime: times.exitTime,
  });

  const withAutoDuration = (updated, entryDate, exitDt) => {
    if (durationEdited) return updated;
    const autoDuration = autoDurationFor(updated, entryDate, exitDt);
    return autoDuration === null ? updated : { ...updated, duration: autoDuration };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'entryTime' || name === 'exitTime') {
      const formatted = formatTimeInput(value);
      setForm((prev) => withAutoDuration({ ...prev, [name]: formatted }, swingEntryDate || logDate, exitDate));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDurationChange = (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, duration: value }));
    setDurationEdited(value.trim() !== '');
  };

  // Handle main entry date change (used for both day and swing trading)
  const handleMainEntryDateChange = (e) => {
    const newLogDate = e.target.value;
    setLogDate(newLogDate);
    if (tradingStyle === 'swing' && !swingEntryDate) {
      setForm(prev => withAutoDuration(prev, newLogDate, exitDate));
    }
  };
  
  // Handle swing trading entry date change (separate from main date field)
  const handleSwingEntryDateChange = (e) => {
    const newEntryDate = e.target.value;
    setSwingEntryDate(newEntryDate);
    setForm(prev => withAutoDuration(prev, newEntryDate, exitDate));
  };
  
  // Handle exit date change for swing trading
  const handleExitDateChange = (e) => {
    const newExitDate = e.target.value;
    setExitDate(newExitDate);
    setForm(prev => withAutoDuration(prev, swingEntryDate || logDate, newExitDate));
  };

  const autoDuration = autoDurationFor(form, swingEntryDate || logDate, exitDate);
  const durationSuggestion = getDurationSuggestion(form.duration, autoDuration);
  const applySuggestedDuration = () => {
    setForm((prev) => ({ ...prev, duration: durationSuggestion }));
    setDurationEdited(false);
  };

  const handleFiles = (files) => {
    readImageFilesAsDataUrls(files).then((images) => {
      if (images.length > 0) {
        setScreenshots(prev => [...prev, ...images]);
      }
    });
  };
  // Drag-and-drop onto the chart area and Ctrl/Cmd+V anywhere in the form both
  // add charts through the same preview/upload path as the file picker.
  const { isDragging: isDraggingChart, dropZoneProps: chartDropZoneProps } = useChartDropZone(handleFiles);
  useDocumentImagePaste(handleFiles);
  useSubmitShortcut(formRef);
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    e.target.value = null;
  };

  const handleRemoveScreenshot = (idx) => {
    setScreenshots(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePasteImages = (e) => {
    const files = getClipboardImageFiles(e);
    if (files.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    handleFiles(files);
  };

  const handleEntryTypeChange = (e) => setEntryType(e.target.value);

  const handleCopyLastTrade = async () => {
    if (!currentUser || !selectedAccount || copyingLastTrade) return;
    const hasMeaningfulValue = (value) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true;
    };
    setCopyingLastTrade(true);
    try {
      const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
      const q = query(entriesCol, orderBy('created', 'desc'), limit(20));
      const snap = await getDocs(q);
      const lastTrade = snap.docs
        .map(d => d.data())
        .find(e => !e.isDeposit && !e.isPayout && !e.isBalanceAdjustment && !e.isResetExcluded && !isTapeReading(e));
      if (!lastTrade) {
        alert('No previous trade found to copy.');
        return;
      }

      setForm(prev => ({
        ...prev,
        tickerTraded: hasMeaningfulValue(lastTrade.tickerTraded) ? lastTrade.tickerTraded : prev.tickerTraded,
        tradeSession: hasMeaningfulValue(lastTrade.tradeSession) ? lastTrade.tradeSession : prev.tradeSession,
        rr: hasMeaningfulValue(lastTrade.rr) ? lastTrade.rr : prev.rr,
        entryTime: hasMeaningfulValue(lastTrade.entryTime) ? lastTrade.entryTime : prev.entryTime,
        exitTime: hasMeaningfulValue(lastTrade.exitTime) ? lastTrade.exitTime : prev.exitTime,
        duration: hasMeaningfulValue(lastTrade.duration) ? lastTrade.duration : prev.duration,
      }));
      if (hasMeaningfulValue(lastTrade.tradeRating)) setTradeRating(lastTrade.tradeRating);
      if (hasMeaningfulValue(lastTrade.pdArray)) setPdArray(lastTrade.pdArray);
      if (hasMeaningfulValue(lastTrade.target)) setTarget(lastTrade.target);
      if (hasMeaningfulValue(lastTrade.economicRelease)) setEconomicRelease(lastTrade.economicRelease);
      if (lastTrade.tradingStyle === 'day' || lastTrade.tradingStyle === 'swing') {
        setTradingStyle(lastTrade.tradingStyle);
      }

      const envCopy = {};
      tradeEnvFields.forEach(field => {
        if (!field.enabled || lastTrade[field.id] === undefined) return;
        const rawValue = lastTrade[field.id];
        if (isAlignmentField(field.id)) {
          const decoded = decodeAlignmentValue(rawValue);
          if (decoded !== null || rawValue === null) envCopy[field.id] = decoded;
          return;
        }
        if (hasMeaningfulValue(rawValue) || rawValue === false || rawValue === null) {
          envCopy[field.id] = rawValue;
        }
      });
      if (Object.keys(envCopy).length > 0) {
        setTradeEnv(prev => ({ ...prev, ...envCopy }));
      }
    } catch (error) {
      console.error('Error copying last trade:', error);
      alert('Failed to copy last trade. Please try again.');
    } finally {
      setCopyingLastTrade(false);
    }
  };

  const saveEntry = async ({ addAnother = false } = {}) => {
    let urls = [];
    if (currentUser && selectedAccount) {
      const uploadBatchId = Date.now();
      try {
        urls = await uploadScreenshotList(screenshots, async (src, idx) => {
          const imageRef = ref(storage, `screenshots/${currentUser.uid}/${selectedAccount.id}/${uploadBatchId}-${idx}.jpg`);
          await uploadString(imageRef, src, 'data_url');
          return getDownloadURL(imageRef);
        });
      } catch (err) {
        if (!(err instanceof ScreenshotUploadError)) throw err;
        // Never save the entry without its charts. Keep the charts that did upload
        // (now URLs) so saving again only re-sends the failed ones.
        setScreenshots(err.images);
        toast.error(`${err.message} The entry was not saved. Click Save to try again, or remove the chart.`);
        return;
      }
      // Keep the uploaded URLs so retrying after a failed save does not upload twice.
      setScreenshots(urls);
    }
    setUploading(false);
    // Use selected log date from state (logDate is in YYYY-MM-DD format)
    // Parse date string directly to avoid timezone issues
    const [year, month, day] = logDate.split('-').map(Number);
    const entryYear = String(year);
    const entryMonth = String(month);
    const entryDay = String(day);
    // created timestamp should still use actual submission time
    const now = new Date();
    const createdTimestamp = now.toISOString() + '-' + Math.random().toString(36).slice(2, 8);
    
    // Prepare exit date fields for swing trading
    let exitYear = null;
    let exitMonth = null;
    let exitDay = null;
    if (tradingStyle === 'swing' && exitDate) {
      const [exYear, exMonth, exDay] = exitDate.split('-').map(Number);
      exitYear = String(exYear);
      exitMonth = String(exMonth);
      exitDay = String(exDay);
    }

    let tradeEntryYear = null;
    let tradeEntryMonth = null;
    let tradeEntryDay = null;
    if (tradingStyle === 'swing') {
      const swingDate = swingEntryDate || logDate;
      if (swingDate) {
        const [enYear, enMonth, enDay] = swingDate.split('-').map(Number);
        tradeEntryYear = String(enYear);
        tradeEntryMonth = String(enMonth);
        tradeEntryDay = String(enDay);
      }
    }
    
    const trimmedEntry = trimFirestoreDoc({
      ...form,
      // 00:00 / 8:30 answers are stored as 'above' / 'below' / null (null when the question is off).
      ...buildTradeEnvForNewEntry(tradeEnv),
      tradeRating: tradeRating || "",
      pdArray: pdArray || "",
      target: target || "",
      economicRelease,
      tradeSession: form.tradeSession,
      title: form.title,
      pnl: entryType === 'trade' ? Number(form.pnl) : 0,
      rr: entryType === 'trade' ? form.rr : '0',
      accountBalance: calculateAccountBalance(savedBalanceRef.current ?? initialAccountBalance, entryType === 'trade' ? Number(form.pnl) : 0),
      screenshots: urls,
      created: createdTimestamp,
      tapeReading: entryType === 'tape',
      isDeposit: false,
      isPayout: false,
      year: entryYear,
      month: entryMonth,
      day: entryDay,
      tradingStyle: tradingStyle,
      ...(entryType === 'trade' && mindsetExecutionBeta && hasMindsetExecutionData(mindsetExecution) ? {
        mindsetExecution: {
          emotions: mindsetExecution.emotions,
          composure: mindsetExecution.composure,
          execution: mindsetExecution.execution.trim(),
          learned: mindsetExecution.learned.trim(),
          distraction: mindsetExecution.distraction.trim(),
          improvement: mindsetExecution.improvement.trim(),
        },
      } : {}),
      ...(tradingStyle === 'swing' && tradeEntryYear && tradeEntryMonth && tradeEntryDay ? {
        entryYear: tradeEntryYear,
        entryMonth: tradeEntryMonth,
        entryDay: tradeEntryDay,
      } : {}),
      ...(tradingStyle === 'swing' && exitYear && exitMonth && exitDay ? {
        exitYear: exitYear,
        exitMonth: exitMonth,
        exitDay: exitDay,
      } : {}),
    });
    
    if (trimmedEntry && currentUser && selectedAccount) {
      const { db } = await import('../firebase');
      const { collection, addDoc, query, orderBy, limit, getDocs } = await import('firebase/firestore');
      const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
      const docRef = await addDoc(entriesCol, trimmedEntry);
      if (addAnother) {
        // Stay on the form for the next entry; refresh the pages underneath in the background.
        savedBalanceRef.current = trimmedEntry.accountBalance;
        triggerDataRefresh?.();
      } else {
        // Pass the saved entry data to onSave callback
        onSave({ ...trimmedEntry, id: docRef.id });
      }
      
      // Check for easter egg triggers: if this was a trade
      if (entryType === 'trade') {
        try {
          // Get last 10 trades for win streak check
          const entriesQuery = query(entriesCol, orderBy('created', 'desc'), limit(10));
          const entriesSnap = await getDocs(entriesQuery);
          const recentTrades = entriesSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(entry => 
              !entry.isDeposit && 
              !entry.isPayout && 
              !isTapeReading(entry) && 
              !entry.isResetExcluded &&
              entry.pnl !== undefined &&
              entry.pnl !== null &&
              entry.pnl !== ""
            );
          
          // Check for 10-trade win streak (positive P&L) - only if enabled
          // Check preference first, then call the function (which also checks)
          if (recentTrades.length >= 10 && easterEggs?.bandzEmail !== false) {
            const allPositive = recentTrades.slice(0, 10).every(entry => Number(entry.pnl) > 0);
            if (allPositive) {
              // Function will check easterEggs.bandzEmail internally
              if (showBandzEmail) {
                showBandzEmail();
              }
              // Save badge achievement state (always save, regardless of toggle)
              if (setEliteBadgeAchieved) {
                setEliteBadgeAchieved(currentUser.uid);
              }
            }
          }
          
          // Check for ICT email trigger: last 5 trades are negative - only if enabled
          // Get last 5 trades for ICT check
          const ictQuery = query(entriesCol, orderBy('created', 'desc'), limit(5));
          const ictSnap = await getDocs(ictQuery);
          const ictTrades = ictSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(entry => 
              !entry.isDeposit && 
              !entry.isPayout && 
              !isTapeReading(entry) && 
              !entry.isResetExcluded &&
              entry.pnl !== undefined &&
              entry.pnl !== null &&
              entry.pnl !== ""
            );
          
          // Check preference first, then call the function (which also checks)
          if (ictTrades.length >= 5 && easterEggs?.ictEmail !== false) {
            const allNegative = ictTrades.every(entry => Number(entry.pnl) < 0);
            if (allNegative) {
              // Function will check easterEggs.ictEmail internally
              if (showICTEmail) {
                showICTEmail();
              }
              // Save achievement state (always save, regardless of toggle)
              if (setICTEmailFound && currentUser) {
                setICTEmailFound(currentUser.uid);
              }
            }
          }
        } catch (error) {
          console.error('Error checking for easter egg triggers:', error);
        }
      }
      
      if (addAnother) {
        resetForAnotherEntry();
        return;
      }
      setForm(initialState);
      setDurationEdited(false);
      setScreenshots([]);
      setPdArray("");
      setTarget("");
      setTradingStyle('day');
      setExitDate('');
      // Reset toggle states to N/A
      setTradeEnv({
        judasSwing: null,
        silverBullet: null,
        manipulation: null,
        smt: null,
      });
      setEconomicRelease("");
      setTradeRating("");
      setMindsetExecution(createEmptyMindsetExecution());
    }
  };

  // A fresh form for the same day and account, ready for the next entry.
  const resetForAnotherEntry = () => {
    setForm({ ...initialState });
    setDurationEdited(false);
    setScreenshots([]);
    setPdArray("");
    setTarget("");
    setTradingStyle('day');
    setSwingEntryDate(logDate);
    setExitDate('');
    setTradeEnv(initializeTradeEnvState(tradeEnvFields));
    setEconomicRelease("");
    setTradeRating("");
    setMindsetExecution(createEmptyMindsetExecution());
    setPristineDate(logDate);
    toast.success('Saved. Ready for the next entry.');
    requestAnimationFrame(() => {
      formRef.current?.querySelector('[data-first-field]')?.focus();
    });
  };

  const runSave = async ({ addAnother = false } = {}) => {
    if (uploading || submitting) return;
    setUploading(true);
    setSubmitting(true);
    try {
      await saveEntry({ addAnother });
    } catch (error) {
      console.error('Error saving entry:', error?.message || error);
      toast.error("Couldn't save this entry. Check your connection and try again.");
    } finally {
      // Always leave the saving state, whatever happened above.
      setUploading(false);
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSave();
  };

  const handleSaveAndAddAnother = () => runSave({ addAnother: true });

  // Unsaved changes: anything typed, picked or attached since the form opened (or was reset).
  const editableForm = { ...form, accountBalance: "" };
  const pristineForm = { ...initialState, accountBalance: "" };
  const isDirty = JSON.stringify({
    form: editableForm, screenshots, tradeRating, pdArray, target, economicRelease, tradeEnv,
    mindsetExecution, tradingStyle, exitDate, logDate, swingEntryDate,
  }) !== JSON.stringify({
    form: pristineForm, screenshots: [], tradeRating: '', pdArray: '', target: '', economicRelease: '',
    tradeEnv: initializeTradeEnvState(tradeEnvFields), mindsetExecution: createEmptyMindsetExecution(),
    tradingStyle: 'day', exitDate: '', logDate: pristineDate, swingEntryDate: pristineDate,
  });
  const leaveIfConfirmed = useUnsavedChangesGuard(isDirty);
  const handleCancel = () => leaveIfConfirmed(onCancel);
  const handleGoToQuick = onGoToQuick
    ? (mode) => leaveIfConfirmed(() => onGoToQuick(mode))
    : undefined;

  const rulesToggleButtonBaseClass = 'w-7 h-7 rounded-btn flex items-center justify-center theme-choice-button';
  const yesRulesButtonClass = `${rulesToggleButtonBaseClass} ${rulesResponseForDate === 'yes' ? 'theme-choice-button-success-active' : 'theme-choice-button-success'}`;
  const noRulesButtonClass = `${rulesToggleButtonBaseClass} ${rulesResponseForDate === 'no' ? 'theme-choice-button-danger-active' : 'theme-choice-button-danger'}`;
  const sessionField = getJournalField(journalFields, 'tradeSession');
  const economicReleaseField = getJournalField(journalFields, 'economicRelease');
  const pdArrayField = getJournalField(journalFields, 'pdArray');
  const targetField = getJournalField(journalFields, 'target');
  const rulesFollowedField = getJournalField(journalFields, 'rulesFollowed');

  const toggleMindsetEmotion = (emotion) => {
    setMindsetExecution((current) => ({
      ...current,
      emotions: current.emotions.includes(emotion)
        ? current.emotions.filter((item) => item !== emotion)
        : [...current.emotions, emotion],
    }));
  };

  const useUnifiedJournal = unifiedJournalBeta
    && (journalModeOverride === 'beta' || journalModeOverride !== 'advanced');

  if (useUnifiedJournal && entryType === 'trade') {
    const prototypeController = {
      accountName: selectedAccount?.name,
      form,
      setForm,
      handleChange,
      logDate,
      handleMainEntryDateChange,
      pnlToneColor,
      tradingStyle,
      setTradingStyle,
      swingEntryDate,
      handleSwingEntryDateChange,
      exitDate,
      setExitDate,
      handleExitDateChange,
      economicRelease,
      setEconomicRelease,
      tradeRating,
      setTradeRating,
      pdArray,
      setPdArray,
      target,
      setTarget,
      tradeEnvFields,
      journalFields,
      tradeEnv,
      setTradeEnv,
      mindsetExecutionBeta,
      mindsetExecution,
      setMindsetExecution,
      screenshots,
      fileInputRef,
      handleFileChange,
      handleDurationChange,
      durationSuggestion,
      applySuggestedDuration,
      formRef,
      chartDropZoneProps,
      isDraggingChart,
      handlePasteImages,
      handleRemoveScreenshot,
      openScreenshot: (index) => {
        setSelectedImageIndex(index);
        setShowImageViewer(true);
      },
      uploading,
      submitting,
      copyingLastTrade,
      handleCopyLastTrade,
      rulesResponseForDate,
      handleRulesResponse,
      handleSubmit,
      onCancel: handleCancel,
      onGoToQuick: handleGoToQuick,
      handleSaveAndAddAnother,
      onGoToAdvanced: () => setJournalModeOverride('advanced'),
    };

    return (
      <>
        <UnifiedJournalPrototype controller={prototypeController} />
        <ZoomableImageModal
          isOpen={showImageViewer && screenshots.length > 0}
          images={screenshots}
          index={selectedImageIndex}
          onClose={() => setShowImageViewer(false)}
          onPrev={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : screenshots.length - 1)}
          onNext={() => setSelectedImageIndex(prev => prev < screenshots.length - 1 ? prev + 1 : 0)}
          showNavigation={screenshots.length > 1}
        />
      </>
    );
  }

  return (
    <>
    <form ref={formRef} onSubmit={handleSubmit} className="fixed inset-0 z-[70] flex flex-col bg-[#FAFAFA] dark:bg-[#111827]">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-[#1F2937] max-w-none w-screen h-screen rounded-none flex flex-col shadow-elevated"
      >
        {/* Sticky Header */}
        <div className="sticky top-0 z-[70] bg-white dark:bg-[#1F2937] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 border-b border-[#E5E7EB] dark:border-[#374151] rounded-none">
            {/* Entry Type Dropdown on the left */}
            <div className="flex w-full sm:w-auto items-center justify-between sm:justify-start gap-3 sm:gap-4">
              <label className="text-base font-semibold text-[#111827] dark:text-[#F9FAFB]">Entry Type</label>
              <select
                value={entryType}
                onChange={handleEntryTypeChange}
                className="bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] transition-all text-sm"
              >
                {entryTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {onGoToQuick && (
              <div role="group" aria-label="Journal mode" className="theme-choice-group flex w-full items-center gap-1 rounded-xl p-1 sm:w-auto">
                <button type="button" onClick={() => handleGoToQuick(entryType)} className="theme-text-muted theme-hover-surface flex-1 rounded-lg px-3 py-2 text-xs font-semibold sm:flex-none">Fast</button>
                <button type="button" aria-pressed="true" className="theme-btn-primary pointer-events-none flex-1 rounded-lg px-3 py-2 text-xs font-semibold sm:flex-none">Advanced</button>
                {entryType === 'trade' && unifiedJournalBeta && (
                  <button type="button" onClick={() => setJournalModeOverride('beta')} className="theme-text-muted theme-hover-surface flex-1 rounded-lg px-3 py-2 text-xs font-semibold sm:flex-none">Beta</button>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-6" style={{ paddingBottom: '300px' }}>
          <div className="flex flex-col pb-64">
            {/* 1. Title Field with Date Picker */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="mb-6">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2 ml-[10px]">Title</label>
                    <input
                      name="title"
                      type="text"
                      data-first-field
                      value={form.title} 
                      onChange={handleChange} 
                      className="w-64 h-10 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] px-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" 
                      placeholder={entryType === 'tape' ? "Title for tape reading..." : "Title for trade..."} 
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2 ml-[10px]">
                      {tradingStyle === 'swing' ? 'Log Date' : 'Date'}
                    </label>
                    <input
                      type="date"
                      value={logDate}
                      onChange={handleMainEntryDateChange}
                      className="h-10 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] px-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2 ml-[10px] invisible">Trading Style</label>
                    <select
                      value={tradingStyle}
                      onChange={(e) => {
                        setTradingStyle(e.target.value);
                        // Reset exit date when switching modes
                        if (e.target.value === 'day') {
                          setExitDate('');
                        }
                      }}
                      className="h-10 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] px-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                    >
                      <option value="day">Day Trading</option>
                      <option value="swing">Swing Trading</option>
                    </select>
                  </div>
                  <div className="ml-auto flex items-end gap-3">
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">Rating</label>
                      <div className="flex rounded-btn overflow-hidden border border-[#E5E7EB] dark:border-[#374151]">
                        {['A', 'B', 'C', 'D'].map(grade => (
                          <button
                            key={grade}
                            type="button"
                            onClick={() => setTradeRating(prev => prev === grade ? '' : grade)}
                            className={`w-10 h-10 text-sm font-bold transition-colors ${
                              tradeRating === grade
                                ? grade === 'A' ? 'theme-success-action'
                                : grade === 'B' ? 'bg-[#3B82F6] text-white'
                                : grade === 'C' ? 'bg-[#F59E0B] text-white'
                                : 'theme-loss-action'
                                : 'bg-white dark:bg-[#1F2937] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#F9FAFB] dark:hover:bg-[#374151]'
                            }`}
                          >
                            {grade}
                          </button>
                        ))}
                      </div>
                    </div>
                    {rulesFollowedField?.enabled !== false && <div className="flex flex-col">
                      <label className="theme-text block text-sm font-medium mb-2">{rulesFollowedField?.label || 'Follow rules?'}</label>
                      <div className="theme-choice-group h-10 flex items-center gap-1.5 sm:gap-2 px-1.5 py-1 sm:px-2 sm:py-1.5 rounded-card">
                        <button
                          type="button"
                          onClick={() => handleRulesResponse('yes')}
                          className={yesRulesButtonClass}
                          title="Yes, I followed my rules"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRulesResponse('no')}
                          className={noRulesButtonClass}
                          title="No, I did not follow my rules"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>}
                  </div>
                </div>
              </div>
            )}

            {/* 2. Trade Details Section - Always Visible */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="bg-white dark:bg-[#1F2937] rounded-card p-6 border border-[#E5E7EB] dark:border-[#374151] shadow-subtle mb-6">
                <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-4">Trade Details</h3>
                <div className="space-y-4">
                  {/* Row 1: Ticker, P&L, R:R (P&L and R:R only for trades) */}
                  <div className={`grid gap-4 ${entryType === 'tape' ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
                    <div data-testid="ticker-field" className={`relative flex flex-col gap-2 ${entryType === 'tape' ? '' : 'col-span-2 sm:col-span-1'}`}>
                      <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Ticker</label>
                      <input
                        name="tickerTraded"
                        type="text"
                        value={form.tickerTraded}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setForm(prev => ({ ...prev, tickerTraded: value }));
                          // Filter suggestions based on input
                          if (value) {
                            const filtered = tickerOptions.filter(opt => 
                              opt.toUpperCase().startsWith(value.toUpperCase())
                            );
                            setTickerSuggestions(filtered);
                            setShowTickerSuggestions(filtered.length > 0);
                          } else {
                            setTickerSuggestions([]);
                            setShowTickerSuggestions(false);
                          }
                        }}
                        onFocus={() => {
                          if (form.tickerTraded) {
                            const filtered = tickerOptions.filter(opt => 
                              opt.toUpperCase().startsWith(form.tickerTraded.toUpperCase())
                            );
                            setTickerSuggestions(filtered);
                            setShowTickerSuggestions(filtered.length > 0);
                          } else {
                            setTickerSuggestions(tickerOptions);
                            setShowTickerSuggestions(true);
                          }
                        }}
                        onBlur={() => {
                          // Delay hiding suggestions to allow click
                          setTimeout(() => setShowTickerSuggestions(false), 200);
                        }}
                        className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                        placeholder="Enter ticker..."
                        list="ticker-options"
                      />
                      {showTickerSuggestions && tickerSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-elevated z-50 max-h-40 overflow-y-auto">
                          {tickerSuggestions.map(opt => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                setForm(prev => ({ ...prev, tickerTraded: opt }));
                                setShowTickerSuggestions(false);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-sm text-[#111827] dark:text-[#F9FAFB]"
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {entryType === 'trade' && (
                      <>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">P&L</label>
                          <div className="relative">
                            <CurrencyDollarIcon
                              data-pnl-tone={pnlTone}
                              className="pnl-tone-text absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]"
                            />
                            <input 
                              name="pnl" 
                              type="number" 
                              step="0.01" 
                              data-pnl-tone={pnlTone}
                              value={form.pnl} 
                              onChange={handleChange} 
                              onWheel={(e) => e.target.blur()} 
                              style={{
                                '--advanced-pnl-tone-color': pnlToneColor,
                              }}
                              className="advanced-pnl-tone-input pnl-tone-input w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:outline-none transition-all text-sm font-normal"
                              placeholder="$" 
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">R:R</label>
                          <input name="rr" type="number" step="0.01" value={form.rr} onChange={handleChange} onWheel={(e) => e.target.blur()} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 2.5" />
                        </div>
                      </>
                    )}
                  </div>
                  {/* Row 2: Entry/Exit Time and Duration - Conditional based on trading style */}
                  {tradingStyle === 'day' ? (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Entry Time</label>
                        <div className="relative">
                          <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                          <input name="entryTime" type="text" value={form.entryTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 09:30" />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Exit Time</label>
                        <div className="relative">
                          <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                          <input name="exitTime" type="text" value={form.exitTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 14:45" />
                        </div>
                      </div>
                      <div data-testid="duration-field" className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                        <div className="flex items-center justify-between gap-2">
                          <label htmlFor="advanced-duration" className="text-xs font-medium text-[#374151] dark:text-[#9CA3AF]">Duration</label>
                          {durationSuggestion && (
                            <button type="button" onClick={applySuggestedDuration} className="text-xs font-medium text-[#3B82F6] hover:underline dark:text-[#60A5FA]">
                              Use {durationSuggestion}
                            </button>
                          )}
                        </div>
                        <input id="advanced-duration" name="duration" type="text" value={form.duration} onChange={handleDurationChange} className="w-full bg-[#F9FAFB] dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="Auto-calculated" />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Entry Date/Time for Swing Trading */}
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Entry Date</label>
                          <div className="relative">
                            <CalendarIcon className="absolute left-3 top-1/2 hidden sm:block transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                            <input 
                              type="date" 
                              value={swingEntryDate} 
                              onChange={handleSwingEntryDateChange}
                              className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-3 sm:pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Entry Time</label>
                          <div className="relative">
                            <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                            <input name="entryTime" type="text" value={form.entryTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 09:30" />
                          </div>
                        </div>
                        <div className="hidden sm:block"></div>
                      </div>
                      {/* Exit Date/Time for Swing Trading */}
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Exit Date</label>
                          <div className="relative">
                            <CalendarIcon className="absolute left-3 top-1/2 hidden sm:block transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                            <input 
                              type="date" 
                              value={exitDate} 
                              onChange={handleExitDateChange}
                              className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-3 sm:pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Exit Time</label>
                          <div className="relative">
                            <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                            <input name="exitTime" type="text" value={form.exitTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 14:45" />
                          </div>
                        </div>
                        <div data-testid="duration-field" className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                          <div className="flex items-center justify-between gap-2">
                            <label htmlFor="advanced-duration" className="text-xs font-medium text-[#374151] dark:text-[#9CA3AF]">Duration</label>
                            {durationSuggestion && (
                              <button type="button" onClick={applySuggestedDuration} className="text-xs font-medium text-[#3B82F6] hover:underline dark:text-[#60A5FA]">
                                Use {durationSuggestion}
                              </button>
                            )}
                          </div>
                          <input id="advanced-duration" name="duration" type="text" value={form.duration} onChange={handleDurationChange} className="w-full bg-[#F9FAFB] dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="Auto-calculated" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 3. Screenshots Dropzone */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="mb-6">
                <div 
                  className={`flex flex-col border-2 border-dashed border-[#E5E7EB] dark:border-[#374151] rounded-card transition-colors cursor-pointer ${
                    screenshots.length === 0 
                      ? 'min-h-28 justify-center'
                      : 'p-4'
                  } ${screenshots.length > 0 ? 'hover:border-[#3B82F6]' : ''}`}
                  style={isDraggingChart ? CHART_DROP_HIGHLIGHT_STYLE : undefined}
                  data-dragging={isDraggingChart ? 'true' : undefined}
                  {...chartDropZoneProps}
                  onPaste={handlePasteImages}
                  onClick={() => fileInputRef.current.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  <input
                    type="text"
                    aria-label="Paste screenshots"
                    value=""
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                    onPaste={handlePasteImages}
                    placeholder="Click here, then Ctrl+V to paste images"
                    className="mx-3 mb-3 min-w-0 self-stretch rounded-card border border-[#D1D5DB] bg-white px-3 py-2 text-xs text-[#111827] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 dark:border-[#4B5563] dark:bg-[#111827] dark:text-[#F9FAFB]"
                  />
                  
                  {screenshots.length === 0 ? (
                    <div className="flex flex-col items-center px-4 py-3 text-center">
                      <PhotoIcon className="mb-2 h-6 w-6 text-[#3B82F6] dark:text-[#60A5FA]" />
                      <div className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">No charts attached yet</div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        className="mt-2 rounded-lg bg-[#3B82F6] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2563EB]"
                      >
                        Add Entry or Exit Chart
                      </button>
                      <div className="mt-2 text-xs text-[#6B7280] dark:text-[#9CA3AF]">Click, drag, or paste an image here.</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[150px]">
                      <div className="flex gap-3">
                        {screenshots.map((src, idx) => (
                          <div key={idx} className="relative group flex-shrink-0">
                            <img 
                              src={src} 
                              alt={`Screenshot ${idx + 1}`} 
                              className="h-32 w-auto object-cover rounded-card border border-[#E5E7EB] dark:border-[#374151] shadow-subtle hover:border-[#3B82F6] transition-all duration-300 cursor-pointer" 
                              onClick={() => {
                                setSelectedImageIndex(idx);
                                setShowImageViewer(true);
                              }}
                            />
                            <button 
                              type="button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveScreenshot(idx);
                              }} 
                              className="absolute top-1 right-1 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-subtle opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 4. Trade Session and Economic Release */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="mb-6">
                <div className="bg-white dark:bg-[#1F2937] rounded-card p-6 border border-[#E5E7EB] dark:border-[#374151] shadow-subtle">
                  <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-4">Trade Details</h3>
                  <div className="space-y-4">
                    {sessionField?.enabled !== false && <div>
                      <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">{sessionField?.label || 'Session'}</label>
                      <ConfigurableJournalInput
                        field={sessionField}
                        name="tradeSession"
                        value={form.tradeSession}
                        onChange={(value) => setForm((current) => ({ ...current, tradeSession: value }))}
                        className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                      />
                    </div>}
                    {economicReleaseField?.enabled !== false && <div>
                      <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">{economicReleaseField?.label || 'Economic release'}</label>
                      <ConfigurableJournalInput
                        field={economicReleaseField}
                        value={economicRelease}
                        onChange={setEconomicRelease}
                        className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                        placeholder="e.g. CPI 8:30, FOMC 14:00"
                      />
                    </div>}
                  </div>
                </div>
              </div>
            )}

            {/* 5. Trade Environment Accordion */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="mt-4">
                <Accordion 
                  title="Trade Environment" 
                  defaultOpen={false}
                >
                  <div className="grid grid-cols-2 gap-4">
                    {tradeEnvFields
                      .filter(field => field.enabled)
                      .map(field => {
                        const fieldType = field.fieldType || 'yesNoNA';
                        
                        if (fieldType === 'text') {
                          return (
                            <div key={field.id} className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">{field.label}</label>
                              <input
                                type="text"
                                value={tradeEnv[field.id] || ''}
                                onChange={(e) => setTradeEnv(te => ({ ...te, [field.id]: e.target.value }))}
                                className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                                inputMode={field.inputMode || undefined}
                                placeholder={field.placeholder || 'Enter text...'}
                              />
                            </div>
                          );
                        } else if (fieldType === 'dropdown') {
                          const options = field.options || [];
                          return (
                            <div key={field.id} className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">{field.label}</label>
                              <select
                                value={tradeEnv[field.id] || ''}
                                onChange={(e) => setTradeEnv(te => ({ ...te, [field.id]: e.target.value }))}
                                className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                            >
                              <option value="">{field.placeholder || 'Select an option'}</option>
                              {options.length > 0 ? (
                                  options.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))
                                ) : (
                                  <option value="">No options defined</option>
                                )}
                              </select>
                            </div>
                          );
                        } else {
                          // yesNoNA - use SegmentedPill
                          return (
                            <SegmentedPill
                              key={field.id}
                              value={tradeEnv[field.id]}
                              onChange={v => setTradeEnv(te => ({ ...te, [field.id]: v }))}
                              label={field.label}
                            />
                          );
                        }
                      })}
                  </div>
                </Accordion>
              </div>
            )}

            {/* Optional Mindset & Execution beta for normal trades */}
            {entryType === 'trade' && mindsetExecutionBeta && (
              <div className="mt-4">
                <Accordion title="Mindset & Execution (Beta)" defaultOpen={false}>
                  <div className="space-y-6">
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">
                          Emotions
                        </label>
                        <span className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">Select any that apply</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {MINDSET_EMOTIONS.map((emotion) => {
                          const selected = mindsetExecution.emotions.includes(emotion);
                          return (
                            <button
                              key={emotion}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => toggleMindsetEmotion(emotion)}
                              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                                selected
                                  ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-[#2563EB] dark:text-[#93C5FD]'
                                  : 'border-[#D1D5DB] bg-white text-[#4B5563] hover:border-[#93C5FD] dark:border-[#4B5563] dark:bg-[#111827] dark:text-[#D1D5DB]'
                              }`}
                            >
                              {emotion}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">
                          Composure
                        </label>
                        <div className="grid grid-cols-5 overflow-hidden rounded-card border border-[#D1D5DB] dark:border-[#4B5563]">
                          {[1, 2, 3, 4, 5].map((score) => (
                            <button
                              key={score}
                              type="button"
                              aria-label={`Composure ${score} of 5`}
                              aria-pressed={mindsetExecution.composure === score}
                              onClick={() => setMindsetExecution((current) => ({
                                ...current,
                                composure: current.composure === score ? null : score,
                              }))}
                              className={`h-11 border-l border-[#D1D5DB] text-sm font-semibold first:border-l-0 dark:border-[#4B5563] ${
                                mindsetExecution.composure === score
                                  ? 'bg-[#3B82F6] text-white'
                                  : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6] dark:bg-[#111827] dark:text-[#D1D5DB] dark:hover:bg-[#374151]'
                              }`}
                            >
                              {score}
                            </button>
                          ))}
                        </div>
                        <div className="mt-1 flex justify-between text-[11px] text-[#6B7280] dark:text-[#9CA3AF]">
                          <span>Rattled</span>
                          <span>Centered</span>
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">
                          Execution
                        </label>
                        <select
                          value={mindsetExecution.execution}
                          onChange={(event) => setMindsetExecution((current) => ({
                            ...current,
                            execution: event.target.value,
                          }))}
                          className="w-full rounded-card border border-[#D1D5DB] bg-white p-3 text-sm text-[#111827] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 dark:border-[#4B5563] dark:bg-[#111827] dark:text-[#F9FAFB]"
                        >
                          <option value="">Select...</option>
                          {EXECUTION_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      {[
                        ['learned', 'What did I learn?'],
                        ['distraction', 'What distracted me?'],
                        ['improvement', 'What could improve?'],
                      ].map(([field, label]) => (
                        <div key={field}>
                          <label className="mb-2 block text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">
                            {label}
                          </label>
                          <textarea
                            rows={3}
                            value={mindsetExecution[field]}
                            onChange={(event) => setMindsetExecution((current) => ({
                              ...current,
                              [field]: event.target.value,
                            }))}
                            className="w-full resize-y rounded-card border border-[#D1D5DB] bg-white p-3 text-sm text-[#111827] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 dark:border-[#4B5563] dark:bg-[#111827] dark:text-[#F9FAFB]"
                            placeholder="Optional"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </Accordion>
              </div>
            )}

            {/* 6. Targets Section Accordion */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="mt-4">
                <Accordion 
                  title="Targets" 
                  defaultOpen={false}
                >
                  <div className="space-y-4">
                    {pdArrayField?.enabled !== false && <div>
                      <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">
                        {pdArrayField?.label || 'Entry model / PD array'}
                      </label>
                      <ConfigurableJournalInput
                        field={pdArrayField}
                        value={pdArray}
                        onChange={setPdArray}
                        className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[var(--theme-success)] focus:border-[var(--theme-success)] focus:outline-none transition-all text-sm font-normal"
                        placeholder="Enter PD array..."
                      />
                    </div>}
                    {targetField?.enabled !== false && <div>
                      <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">
                        {targetField?.label || 'Target'}
                      </label>
                      <ConfigurableJournalInput
                        field={targetField}
                        value={target}
                        onChange={setTarget}
                        className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[var(--theme-success)] focus:border-[var(--theme-success)] focus:outline-none transition-all text-sm font-normal"
                        placeholder="e.g. BSL/SSL, PD array, HTF level, daily high/low..."
                      />
                    </div>}
                  </div>
                </Accordion>
              </div>
            )}

            {/* 7. Trade Notes - Always Visible */}
            {(entryType === 'trade' || entryType === 'tape') && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">{entryType === 'tape' ? 'Tape Reading Notes' : 'Trade Notes'}</label>
                <textarea 
                  name="notes" 
                  value={form.notes} 
                  onChange={handleChange} 
                  className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-4 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all resize-y min-h-[120px] text-sm font-normal leading-relaxed" 
                  placeholder={entryType === 'tape' ? "Enter your tape reading notes here..." : "Enter your trade notes here..."}
                />
              </div>
            )}
          </div>
          </div>

        {/* 8. Sticky Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-[#1F2937] border-t border-[#E5E7EB] dark:border-[#374151] p-4 z-[70] flex justify-between items-center">
          <motion.button 
            whileHover={{ scale: 1.04 }} 
            whileTap={{ scale: 0.98 }} 
            type="button" 
            className="btn-secondary" 
            onClick={handleCancel}
            disabled={uploading}
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap font-medium rounded-card border border-[#E5E7EB] dark:border-[#374151] text-[#374151] dark:text-[#D1D5DB] bg-[#F9FAFB] dark:bg-[#374151] hover:bg-[#F3F4F6] dark:hover:bg-[#4B5563] transition-colors"
            onClick={handleCopyLastTrade}
            disabled={copyingLastTrade || uploading || submitting}
          >
            {copyingLastTrade ? 'Copying...' : 'Copy Last Trade'}
          </motion.button>
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              aria-label="Save and add another"
              title="Save this entry and start a new one for the same day"
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap font-medium rounded-card border border-[#E5E7EB] dark:border-[#374151] text-[#374151] dark:text-[#D1D5DB] bg-[#F9FAFB] dark:bg-[#374151] hover:bg-[#F3F4F6] dark:hover:bg-[#4B5563] transition-colors disabled:opacity-50"
              onClick={handleSaveAndAddAnother}
              disabled={uploading || submitting}
            >
              <span className="sm:hidden" aria-hidden="true">+</span>
              <span className="hidden sm:inline">Save &amp; add another</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="btn-primary"
              disabled={uploading || submitting}
            >
              {uploading ? 'Uploading...' : submitting ? 'Saving...' : 'Save'}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </form>

    <ZoomableImageModal
      isOpen={showImageViewer && screenshots.length > 0}
      images={screenshots}
      index={selectedImageIndex}
      onClose={() => setShowImageViewer(false)}
      onPrev={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : screenshots.length - 1)}
      onNext={() => setSelectedImageIndex(prev => prev < screenshots.length - 1 ? prev + 1 : 0)}
      showNavigation={screenshots.length > 1}
    />
    </>
  );
};

export default JournalEntryForm; 
